import { useState, useRef, useEffect } from 'react';
import { Clock, ChevronDown } from 'lucide-react';

interface TimePickerInputProps {
  value: string;
  onChange: (time: string) => void;
  className?: string;
}

function generateTimeOptions(): string[] {
  const times: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const h = hour.toString().padStart(2, '0');
      const m = minute.toString().padStart(2, '0');
      times.push(`${h}:${m}`);
    }
  }
  return times;
}

function formatTime(time: string): string {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours);
  const period = h >= 12 ? 'PM' : 'AM';
  const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${displayHour}:${minutes} ${period}`;
}

export function TimePickerInput({ value, onChange, className = '' }: TimePickerInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);
  const timeOptions = generateTimeOptions();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: 'center', behavior: 'instant' });
    }
  }, [isOpen]);

  const handleSelect = (time: string) => {
    onChange(time);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2.5 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1875FF]/50 focus:border-[#1875FF] text-sm cursor-pointer transition-all hover:border-[#1875FF]/40 hover:shadow-lg hover:shadow-[#1875FF]/10 focus:shadow-xl focus:shadow-[#1875FF]/20 font-medium text-gray-900 flex items-center justify-between group"
      >
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-[#1875FF]" />
          <span>{formatTime(value)}</span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-[#1875FF] transition-all duration-300 group-hover:scale-110 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-2xl shadow-[#1875FF]/20 overflow-hidden animate-fadeIn">
          <div className="max-h-64 overflow-y-auto custom-scrollbar">
            {timeOptions.map((time) => {
              const isSelected = time === value;
              return (
                <button
                  key={time}
                  ref={isSelected ? selectedRef : null}
                  type="button"
                  onClick={() => handleSelect(time)}
                  className={`w-full px-4 py-2.5 text-left transition-colors font-medium text-sm border-t border-gray-100 first:border-t-0 ${
                    isSelected
                      ? 'bg-[#1875FF]/10 text-[#1875FF]'
                      : 'text-gray-900 hover:bg-[#1875FF]/5 hover:text-[#1875FF]'
                  }`}
                >
                  {formatTime(time)}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
